import { useState, useEffect, useCallback } from 'react';
import { SessionController } from './SessionController';
import { createGlobalState } from 'services/createGlobalState';
import { OIDCClient } from 'infrastructure/oidc/OIDCClient';
import { cancellable } from 'services/utility/cancellable';

const {
    useWithState,
    useState: useRawSessionController,
} = createGlobalState<SessionController | null>((_, b) => b, null);

export function useWithSessionControllerContext() {
    return useWithState(() => new SessionController());
}

export function useSessionController() {
    const controller = useRawSessionController();
    if (!controller) {
        throw new Error('useSessionRequest() from outside of its viable region');
    }
    return controller;
}

export type SessionRequest = 'signin' | 'signout' | 'stay';

export function useSessionRequest(oidcClient: OIDCClient) {
    const controller = useSessionController();

    const [request, setRequest] = useState<SessionRequest>();

    useEffect(() => {
        if (!oidcClient.tokenSet) {
            setRequest('signin');
        } else {
            setRequest('stay');
        }
    }, [oidcClient, setRequest]);

    useEffect(() => {
        const listener = () => setRequest('signin');
        controller.addEventListener('signin', listener);
        return () => controller.removeEventListener('signin', listener);
    }, [controller]);

    useEffect(() => {
        const listener = () => setRequest('signout');
        controller.addEventListener('signout', listener);
        return () => controller.removeEventListener('signout', listener);
    }, [controller]);

    return request;
}

export function useHookSessionRequest(rawRequest: SessionRequest | undefined, target: SessionRequest, cb: () => Promise<void>) {
    const [request, setArrangedRequest] = useState<SessionRequest>();
    useEffect(() => {
        if (rawRequest === undefined) {
            setArrangedRequest(undefined);
            return undefined;
        } else {
            if (rawRequest === target) {
                return cancellable(async isCanceled => {
                    await cb();
                    if (isCanceled()) return;
                    setArrangedRequest(target);
                });
            } else {
                setArrangedRequest(rawRequest);
                return undefined;
            }
        }
    }, [cb, rawRequest, target]);

    return request;
}

export function useSignOut() {
    const controller = useSessionController();
    return useCallback(() => controller.signOut(), [controller]);
}
